// One article: its cover and title, the body in the reading face, the series beside it on wide
// screens, and the next article (or, at the end of a series, the marketplace) after it.
import { ArrowLeft, ArrowRight, LinkSimple } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { Link, useParams } from "react-router";

import { ApiError } from "../api/client";
import { usePost } from "../api/hooks";
import type { PostSummary } from "../api/types";
import { Markdown } from "../markdown/Markdown";
import { useTitle } from "../ui/hooks";
import { Failed, Loading } from "../ui/Section";
import { longDate, partLabel } from "./blog/format";
import { ReadingProgress } from "./blog/ReadingProgress";
import { SeriesNav } from "./blog/SeriesNav";
import { NotFound } from "./NotFound";

function CopyLink() {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 font-mono text-[12px] uppercase tracking-wider text-ink3 hover:text-ink">
      <LinkSimple weight="bold" className="h-3.5 w-3.5" aria-hidden /> {copied ? "Copied" : "Copy link"}
    </button>
  );
}

function NextUp({ next }: { next: PostSummary | null }) {
  if (!next) {
    return (
      <Link to="/workflows" className="group block rounded-3xl bg-ink p-8 text-paper sm:p-10">
        <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-paper/50">Where the series leads</p>
        <p className="display mt-3 text-4xl sm:text-5xl">Browse the workflow marketplace.</p>
        <span className="mt-6 inline-flex items-center gap-2 font-semibold text-signal">
          Published workflows and their versions <ArrowRight weight="bold" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    );
  }
  return (
    <Link to={`/blog/${next.slug}`} className="group grid overflow-hidden rounded-3xl border border-ink bg-card sm:grid-cols-[1fr_1.3fr]">
      {next.cover ? <img src={next.cover} alt="" className="h-full max-h-64 w-full border-b border-line object-cover sm:max-h-none sm:border-b-0 sm:border-e" /> : null}
      <div className="p-7 sm:p-9">
        <p className="kicker">Read next{next.part ? ` · part ${next.part}` : ""}</p>
        <p className="display mt-3 text-4xl">{next.title}</p>
        <p className="mt-3 text-[15.5px] text-ink2">{next.excerpt}</p>
        <span className="mt-5 inline-flex items-center gap-2 font-semibold group-hover:text-signal">
          Continue <ArrowRight weight="bold" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function Post() {
  const { slug = "" } = useParams();
  const page = usePost(slug);
  const article = useRef<HTMLElement>(null);
  useTitle(page.data?.post.title);

  if (page.isPending) return <div className="wrap"><Loading label="Loading the article" /></div>;
  if (page.error instanceof ApiError && page.error.status === 404) return <NotFound />;
  if (!page.data) return <div className="wrap"><Failed what="this article" retry={() => void page.refetch()} /></div>;
  const { post, next, previous } = page.data;
  const part = partLabel(post.part, post.series);

  return (
    <article ref={article}>
      <ReadingProgress target={article} />
      <header className="grain border-b border-line">
        <div className="wrap pb-10 pt-10 sm:pt-16">
          <Link to="/blog" className="inline-flex items-center gap-1.5 font-mono text-[12px] uppercase tracking-wider text-ink3 hover:text-ink">
            <ArrowLeft weight="bold" className="h-3.5 w-3.5" aria-hidden /> Blog
          </Link>
          <div className="mx-auto mt-8 max-w-4xl text-center">
            {part ? <p className="kicker">{part}</p> : null}
            <h1 className="display mt-4 text-[3rem] sm:text-[4.6rem] lg:text-[5.4rem]">{post.title}</h1>
            {post.subtitle ? <p className="mx-auto mt-5 max-w-2xl font-serif text-[1.35rem] italic leading-snug text-ink2 sm:text-[1.55rem]">{post.subtitle}</p> : null}
            <p className="mt-7 font-mono text-[12px] uppercase tracking-wider text-ink3">
              {post.author} · {longDate(post.published_at)} · {post.reading_minutes} min read
            </p>
          </div>
          {post.cover ? (
            <figure className="mx-auto mt-10 max-w-5xl">
              <img src={post.cover} alt={post.cover_alt ?? ""} className="aspect-[2/1] w-full rounded-3xl border border-line bg-paper2 object-cover" />
              {post.cover_credit ? <figcaption className="mt-2 text-end font-mono text-[10.5px] uppercase tracking-wide text-ink3">{post.cover_credit}</figcaption> : null}
            </figure>
          ) : null}
        </div>
      </header>
      <div className="wrap grid gap-12 py-12 lg:grid-cols-[1fr_minmax(0,41rem)_1fr]">
        <div className="hidden lg:block" />
        <div>
          <Markdown source={post.body_md} />
          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
            <div className="flex flex-wrap gap-1.5">
              {post.tags.map((t) => (
                <span key={t} className="chip">
                  {t}
                </span>
              ))}
            </div>
            <CopyLink />
          </div>
        </div>
        <aside className="lg:sticky lg:top-24 lg:self-start">{post.series ? <SeriesNav series={post.series} current={post.slug} /> : null}</aside>
      </div>
      <div className="wrap max-w-5xl">
        <NextUp next={next} />
        {previous ? (
          <p className="mt-6 text-center text-[14.5px] text-ink2">
            Previously:{" "}
            <Link to={`/blog/${previous.slug}`} className="link">
              {previous.title}
            </Link>
          </p>
        ) : null}
      </div>
    </article>
  );
}
